import axios from 'axios'
import clsx from 'clsx'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import {Chart} from 'react-chartjs-2'
import {KTCard, KTCardBody, KTIcon} from '../../../../../_websoft/helpers'
import {
  proizvodnjaPanelaPoDrzaviDetaljiIzvjestaj,
  proizvodnjaPanelaPoDrzaviIzvjestaj,
} from '../../../../api/izvjestaj'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import TitleComponent from '../../../../components/TitleComponent/TitleComponent'

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend)

type ReportRow = {
  drzava?: string | null
  ukupna_kolicina?: string | number | null
  prethodna_kolicina?: string | number | null
  prije_dvije_godine_kolicina?: string | number | null
  razlika_kolicina?: string | number | null
  razlika_prije_dvije_godine_kolicina?: string | number | null
  promjena_postotak?: string | number | null
  udio_postotak?: string | number | null
}

type ReportDataset = {
  label?: string
  data?: Array<string | number | null>
  backgroundColor?: string | string[]
  borderColor?: string | string[]
  borderWidth?: number
}

type ReportResponse = {
  data?: ReportRow[]
  chart?: {
    labels?: Array<string | number>
    datasets?: ReportDataset[]
  }
}

type DetailRow = {
  drzava?: string | null
  tip_panela?: string | null
  izolacija_panela?: string | null
  ukupna_kolicina?: string | number | null
  prethodna_kolicina?: string | number | null
  prije_dvije_godine_kolicina?: string | number | null
  razlika_kolicina?: string | number | null
  razlika_prije_dvije_godine_kolicina?: string | number | null
  promjena_postotak?: string | number | null
  promjena_prije_dvije_godine_postotak?: string | number | null
  udio_postotak?: string | number | null
  kolicina?: string | number | null
  total_kolicina?: string | number | null
  ukupno?: string | number | null
}

type DetailSummary = {
  ukupna_kolicina?: string | number | null
  prethodna_kolicina?: string | number | null
  prije_dvije_godine_kolicina?: string | number | null
  razlika_kolicina?: string | number | null
  promjena_postotak?: string | number | null
  razlika_prije_dvije_godine_kolicina?: string | number | null
  promjena_prije_dvije_godine_postotak?: string | number | null
  udio_postotak?: string | number | null
}

type DetailGroup = {
  drzava: string
  sazetak: DetailSummary | null
  stavke: DetailRow[]
}

type DetailResponse = {
  grupe: DetailGroup[]
}

type ReportTab = 'pregled' | 'grafikoni' | 'detalji'

const chartColors = ['#1b84ff', '#50cd89', '#f6c000', '#f1416c', '#7239ea']
const numberFormatter = new Intl.NumberFormat('bs-BA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatDisplayDate = (date: string) => {
  const [year, month, day] = date.split('-')

  return year && month && day ? `${day}-${month}-${year}` : date
}

const today = new Date()
const defaultDatumOd = `${today.getFullYear()}-01-01`
const defaultDatumDo = formatDate(today)

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null
  }

  const parsed = Number(typeof value === 'string' ? value.replace(',', '.') : value)

  return Number.isFinite(parsed) ? parsed : null
}

const formatDecimal = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return parsed === null ? '-' : numberFormatter.format(parsed)
}

const formatPercent = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return parsed === null ? '-' : `${numberFormatter.format(parsed)}%`
}

const formatDifference = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  if (parsed === null) {
    return '-'
  }

  const sign = parsed > 0 ? '+' : parsed < 0 ? '-' : ''

  return `${sign}${numberFormatter.format(Math.abs(parsed))}`
}

const differenceBetween = (
  currentValue: string | number | null | undefined,
  previousValue: string | number | null | undefined
) => {
  const current = toNumber(currentValue)
  const previous = toNumber(previousValue)

  return current !== null && previous !== null ? current - previous : null
}

const getCurrentDifference = (row: ReportRow) =>
  row.razlika_kolicina ?? differenceBetween(row.ukupna_kolicina, row.prethodna_kolicina)

const getPreviousDifference = (row: ReportRow) =>
  row.razlika_prije_dvije_godine_kolicina ??
  differenceBetween(row.prethodna_kolicina, row.prije_dvije_godine_kolicina)

const sumValues = <T,>(
  rows: T[],
  getValue: (row: T) => string | number | null | undefined
) => {
  let total = 0
  let hasValue = false

  rows.forEach((row) => {
    const value = toNumber(getValue(row))

    if (value !== null) {
      total += value
      hasValue = true
    }
  })

  return hasValue ? total : null
}

const calculatePercentChange = (
  currentValue: string | number | null | undefined,
  previousValue: string | number | null | undefined
) => {
  const current = toNumber(currentValue)
  const previous = toNumber(previousValue)

  return current !== null && previous !== null && previous !== 0
    ? ((current - previous) / Math.abs(previous)) * 100
    : null
}

const getYearLabel = (datumOd: string, datumDo: string, offset = 0) => {
  const godinaOd = Number(datumOd.slice(0, 4))
  const godinaDo = Number(datumDo.slice(0, 4))

  if (!Number.isFinite(godinaOd) || !Number.isFinite(godinaDo)) {
    return ''
  }

  const from = godinaOd + offset
  const to = godinaDo + offset

  return from === to ? String(from) : `${from}-${to}`
}

const differenceClass = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return clsx({
    'text-success': parsed !== null && parsed > 0,
    'text-danger': parsed !== null && parsed < 0,
    'text-gray-700': parsed === null || parsed === 0,
  })
}

const getCountryName = (row: ReportRow) => row.drzava?.trim() || 'Nepoznata drzava'

const getDetailCountryName = (row: DetailRow) => row.drzava?.trim() || 'Nepoznata drzava'

const getDetailPanelType = (row: DetailRow) => row.tip_panela?.trim() || 'Nije definirano'

const getDetailIsolationName = (row: DetailRow) =>
  row.izolacija_panela?.trim() || 'Nije definirano'

const getDetailQuantity = (row: DetailRow) =>
  row.ukupna_kolicina ?? row.kolicina ?? row.total_kolicina ?? row.ukupno ?? 0

const getDetailCurrentDifference = (row: DetailRow) =>
  row.razlika_kolicina ?? differenceBetween(row.ukupna_kolicina, row.prethodna_kolicina)

const getDetailPreviousDifference = (row: DetailRow) =>
  row.razlika_prije_dvije_godine_kolicina ??
  differenceBetween(row.prethodna_kolicina, row.prije_dvije_godine_kolicina)

const normalizeResponse = (responseData: any): ReportResponse => {
  if (
    responseData?.data &&
    !Array.isArray(responseData.data) &&
    (responseData.data.data || responseData.data.chart)
  ) {
    return responseData.data
  }

  return responseData ?? {}
}

const normalizeDetailsResponse = (responseData: any): DetailResponse => {
  const payload =
    responseData?.data &&
    !Array.isArray(responseData.data) &&
    (responseData.data.data || responseData.data.items || responseData.data.stavke)
      ? responseData.data
      : responseData
  const rawRows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
        ? payload.items
        : []
  const globalSummary = payload?.sazetak ?? payload?.summary ?? null
  const groups = new Map<string, DetailGroup>()

  rawRows.forEach((rawRow: any) => {
    const country = String(rawRow?.drzava ?? 'Nepoznata drzava').trim() || 'Nepoznata drzava'
    const group = groups.get(country) ?? {drzava: country, sazetak: null, stavke: []}
    const nestedItems = Array.isArray(rawRow?.stavke)
      ? rawRow.stavke
      : Array.isArray(rawRow?.items)
        ? rawRow.items
        : Array.isArray(rawRow?.details)
          ? rawRow.details
          : null
    const summary = rawRow?.sazetak ?? rawRow?.summary ?? null

    if (nestedItems) {
      group.stavke.push(...nestedItems)
      group.sazetak = summary ?? group.sazetak
    } else if (rawRow?.tip_panela || rawRow?.izolacija_panela) {
      group.stavke.push(rawRow)
    } else if (summary || rawRow?.ukupna_kolicina || rawRow?.prethodna_kolicina) {
      group.sazetak = summary ?? rawRow
    }

    groups.set(country, group)
  })

  if (globalSummary && groups.size === 1) {
    const group = Array.from(groups.values())[0]
    group.sazetak = group.sazetak ?? globalSummary
  }

  return {grupe: Array.from(groups.values())}
}

const getDetailSummary = (group: DetailGroup): DetailSummary => {
  const current = group.sazetak?.ukupna_kolicina ?? sumValues(group.stavke, getDetailQuantity)
  const previous =
    group.sazetak?.prethodna_kolicina ??
    sumValues(group.stavke, (row) => row.prethodna_kolicina)
  const beforeTwoYears =
    group.sazetak?.prije_dvije_godine_kolicina ??
    sumValues(group.stavke, (row) => row.prije_dvije_godine_kolicina)
  const currentDifference =
    group.sazetak?.razlika_kolicina ??
    sumValues(group.stavke, getDetailCurrentDifference)
  const previousDifference =
    group.sazetak?.razlika_prije_dvije_godine_kolicina ??
    sumValues(group.stavke, getDetailPreviousDifference)

  return {
    ukupna_kolicina: current,
    prethodna_kolicina: previous,
    prije_dvije_godine_kolicina: beforeTwoYears,
    razlika_kolicina: currentDifference,
    promjena_postotak:
      group.sazetak?.promjena_postotak ?? calculatePercentChange(current, previous),
    razlika_prije_dvije_godine_kolicina: previousDifference,
    promjena_prije_dvije_godine_postotak:
      group.sazetak?.promjena_prije_dvije_godine_postotak ??
      calculatePercentChange(previous, beforeTwoYears),
    udio_postotak: group.sazetak?.udio_postotak ?? (group.stavke.length > 0 ? 100 : null),
  }
}

const getDetailShare = (row: DetailRow, totalCurrent: string | number | null | undefined) => {
  if (row.udio_postotak !== null && typeof row.udio_postotak !== 'undefined') {
    return row.udio_postotak
  }

  const quantity = toNumber(getDetailQuantity(row))
  const total = toNumber(totalCurrent)

  return quantity !== null && total !== null && total !== 0 ? (quantity / total) * 100 : null
}

const chartOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label ?? ''}: ${formatDecimal(context.parsed.y)}`,
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Drzava',
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Kolicina',
      },
      ticks: {
        callback: (value) => formatDecimal(Number(value)),
      },
    },
  },
}

const IzvjestajPoDrzavi: React.FC = () => {
  const [datumOd, setDatumOd] = useState(defaultDatumOd)
  const [datumDo, setDatumDo] = useState(defaultDatumDo)
  const [activeTab, setActiveTab] = useState<ReportTab>('pregled')
  const [activeDetailCountry, setActiveDetailCountry] = useState<string | null>(null)
  const [report, setReport] = useState<ReportResponse | null>(null)
  const [detailReport, setDetailReport] = useState<DetailResponse | null>(null)
  const [confirmedPeriod, setConfirmedPeriod] = useState<{datumOd: string; datumDo: string} | null>(null)
  const [loading, setLoading] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [error, setError] = useState('')
  const [detailsError, setDetailsError] = useState('')
  const abortControllerRef = useRef<AbortController | null>(null)
  const detailsAbortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      detailsAbortControllerRef.current?.abort()
    }
  }, [])

  const handleConfirm = async () => {
    if (!datumOd || !datumDo) {
      setError('Datum od i datum do su obavezni.')
      return
    }

    if (datumOd > datumDo) {
      setError('Datum od ne moze biti veci od datuma do.')
      return
    }

    abortControllerRef.current?.abort()

    const controller = new AbortController()
    abortControllerRef.current = controller
    setLoading(true)
    setError('')
    setConfirmedPeriod({datumOd, datumDo})
    setDetailReport(null)
    setDetailsError('')

    try {
      const response = await proizvodnjaPanelaPoDrzaviIzvjestaj(
        {datumOd, datumDo},
        controller.signal
      )

      setReport(normalizeResponse(response.data))
    } catch (requestError: any) {
      if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
        return
      }

      setReport(null)
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          'Greska prilikom ucitavanja izvjestaja.'
      )
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (activeTab !== 'detalji' || !confirmedPeriod) {
      return
    }

    detailsAbortControllerRef.current?.abort()

    const controller = new AbortController()
    let isActive = true
    detailsAbortControllerRef.current = controller
    setDetailsLoading(true)
    setDetailsError('')

    const loadDetails = async () => {
      try {
        const response = await proizvodnjaPanelaPoDrzaviDetaljiIzvjestaj(
          confirmedPeriod,
          controller.signal
        )

        if (isActive) {
          setDetailReport(normalizeDetailsResponse(response.data))
        }
      } catch (requestError: any) {
        if (!isActive || axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
          return
        }

        setDetailReport(null)
        setDetailsError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            'Greska prilikom ucitavanja detaljnog izvjestaja.'
        )
      } finally {
        if (isActive) {
          setDetailsLoading(false)
        }
      }
    }

    loadDetails()

    return () => {
      isActive = false
      controller.abort()
    }
  }, [activeTab, confirmedPeriod])

  const sortedRows = useMemo(() => {
    return [...(report?.data ?? [])].sort(
      (first, second) =>
        (toNumber(second.ukupna_kolicina) ?? 0) - (toNumber(first.ukupna_kolicina) ?? 0)
    )
  }, [report])

  const chartData = useMemo<ChartData<'bar'>>(() => {
    const chart = report?.chart

    return {
      labels: chart?.labels ?? [],
      datasets: (chart?.datasets ?? []).map((dataset, index) => ({
        label: dataset.label || `Serija ${index + 1}`,
        data: (dataset.data ?? []).map((value) => toNumber(value) ?? 0),
        backgroundColor: dataset.backgroundColor || chartColors[index % chartColors.length],
        borderColor: dataset.borderColor || chartColors[index % chartColors.length],
        borderWidth: dataset.borderWidth ?? 1,
      })),
    }
  }, [report])

  const hasChartData = chartData.labels && chartData.labels.length > 0 && chartData.datasets.length > 0
  const hasData = (report?.data ?? []).length > 0
  const currentYearLabel = getYearLabel(datumOd, datumDo)
  const previousYearLabel = getYearLabel(datumOd, datumDo, -1)
  const twoYearsAgoYearLabel = getYearLabel(datumOd, datumDo, -2)
  const totals = useMemo(() => {
    const current = sumValues(sortedRows, (row) => row.ukupna_kolicina)
    const previous = sumValues(sortedRows, (row) => row.prethodna_kolicina)

    return {
      current,
      previous,
      currentDifference: sumValues(sortedRows, getCurrentDifference),
      beforeTwoYears: sumValues(sortedRows, (row) => row.prije_dvije_godine_kolicina),
      previousDifference: sumValues(sortedRows, getPreviousDifference),
      percentChange: calculatePercentChange(current, previous),
      share: sortedRows.length > 0 ? 100 : null,
    }
  }, [sortedRows])

  const detailGroups = useMemo(
    () =>
      (detailReport?.grupe ?? []).map((group) => ({
        ...group,
        summary: getDetailSummary(group),
      })),
    [detailReport]
  )

  const hasDetailData = detailGroups.length > 0
  const selectedDetailGroup =
    detailGroups.find((group) => group.drzava === activeDetailCountry) ?? detailGroups[0] ?? null

  const detailMatrix = useMemo(() => {
    if (!selectedDetailGroup) {
      return {columns: [], rows: [], totals: {}, grandTotal: 0}
    }

    type MatrixValues = {
      current: number
      previous: number
      beforeTwoYears: number
    }

    const matrix = new Map<string, Record<string, MatrixValues>>()
    const isolationNames = new Set<string>()

    selectedDetailGroup.stavke.forEach((item) => {
      const panelType = getDetailPanelType(item)
      const isolation = getDetailIsolationName(item)
      const panelValues = matrix.get(panelType) ?? {}
      const values = panelValues[isolation] ?? {current: 0, previous: 0, beforeTwoYears: 0}

      isolationNames.add(isolation)
      values.current += (toNumber(getDetailQuantity(item)) ?? 0)
      values.previous += (toNumber(item.prethodna_kolicina) ?? 0)
      values.beforeTwoYears += (toNumber(item.prije_dvije_godine_kolicina) ?? 0)
      panelValues[isolation] = values
      matrix.set(panelType, panelValues)
    })

    const columns = Array.from(isolationNames).sort((first, second) =>
      first.localeCompare(second, 'bs')
    )
    const rows = Array.from(matrix.entries())
      .map(([panelType, values]) => ({
        panelType,
        values,
        total: columns.reduce((sum, column) => sum + (values[column]?.current ?? 0), 0),
      }))
      .sort((first, second) => second.total - first.total)
    const totals = columns.reduce<Record<string, MatrixValues>>((result, column) => {
      result[column] = rows.reduce(
        (sum, row) => ({
          current: sum.current + (row.values[column]?.current ?? 0),
          previous: sum.previous + (row.values[column]?.previous ?? 0),
          beforeTwoYears: sum.beforeTwoYears + (row.values[column]?.beforeTwoYears ?? 0),
        }),
        {current: 0, previous: 0, beforeTwoYears: 0}
      )
      return result
    }, {})

    return {
      columns,
      rows,
      totals,
      grandTotal: rows.reduce((sum, row) => sum + row.total, 0),
    }
  }, [selectedDetailGroup])

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <TitleComponent name='Izvjestaj po drzavi' />
        </div>
      </div>

      <KTCardBody className='border-top p-9'>
        <div className='row mb-8 align-items-end'>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
            <label className='form-label fw-bold fs-6'>Datum od</label>
            <input
              type='date'
              name='datumOd'
              className='form-control form-control-lg form-control-solid'
              value={datumOd}
              onChange={(event) => setDatumOd(event.target.value)}
            />
          </div>

          <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
            <label className='form-label fw-bold fs-6'>Datum do</label>
            <input
              type='date'
              name='datumDo'
              className='form-control form-control-lg form-control-solid'
              value={datumDo}
              onChange={(event) => setDatumDo(event.target.value)}
            />
          </div>

          <div className='col-lg-2 col-md-6 col-sm-12 mb-6'>
            <button
              type='button'
              className='btn btn-primary w-100'
              disabled={loading}
              onClick={handleConfirm}
            >
              {loading ? (
                <>
                  Ucitavanje...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </>
              ) : (
                <>
                  <KTIcon iconName='check' className='fs-2' />
                  Potvrdi
                </>
              )}
            </button>
          </div>
        </div>

        <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-8 fs-6'>
          <li className='nav-item'>
            <button
              type='button'
              className={`nav-link fw-bold ${activeTab === 'pregled' ? 'active' : ''}`}
              onClick={() => setActiveTab('pregled')}
            >
              <KTIcon iconName='element-plus' className='fs-3 me-2' />
              Pregled po drzavi
            </button>
          </li>
          <li className='nav-item'>
            <button
              type='button'
              className={`nav-link fw-bold ${activeTab === 'grafikoni' ? 'active' : ''}`}
              onClick={() => setActiveTab('grafikoni')}
            >
              <KTIcon iconName='chart-simple' className='fs-3 me-2' />
              Grafikoni
            </button>
          </li>
          <li className='nav-item'>
            <button
              type='button'
              className={`nav-link fw-bold ${activeTab === 'detalji' ? 'active' : ''}`}
              onClick={() => setActiveTab('detalji')}
            >
              <KTIcon iconName='document' className='fs-3 me-2' />
              Detalji
            </button>
          </li>
        </ul>

        {error && (
          <div className='notice d-flex bg-light-danger rounded border-danger border border-dashed p-6 mb-8'>
            <div className='fw-bold text-gray-800'>{error}</div>
          </div>
        )}

        {loading && !report && <LoadingComponent />}

        {!loading && !error && !report && (
          <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
            <div className='fw-bold text-gray-700'>Odaberite datume i kliknite Potvrdi.</div>
          </div>
        )}

        {!loading && !error && report && !hasData && (
          <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
            <div className='fw-bold text-gray-700'>Nema podataka za odabrani period.</div>
          </div>
        )}

        {activeTab === 'pregled' && hasData && (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gy-4'>
              <thead>
                <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                  <th className='text-white ps-4'>Drzava</th>
                  <th className='text-white text-end'>{currentYearLabel || 'Trenutna godina'}</th>
                  <th className='text-white text-end'>{previousYearLabel || 'Prethodna godina'}</th>
                  <th className='text-white text-end'>
                    Razlika {currentYearLabel && previousYearLabel && `${currentYearLabel} / ${previousYearLabel}`}
                  </th>
                  <th className='text-white text-end'>{twoYearsAgoYearLabel || 'Prije dvije godine'}</th>
                  <th className='text-white text-end'>
                    Razlika {previousYearLabel && twoYearsAgoYearLabel && `${previousYearLabel} / ${twoYearsAgoYearLabel}`}
                  </th>
                  <th className='text-white text-end'>Promjena %</th>
                  <th className='text-white text-end pe-4'>Udio %</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, index) => (
                  <tr key={`${getCountryName(row)}-${index}`}>
                    <td className='ps-4 fw-bold text-gray-900'>{getCountryName(row)}</td>
                    <td className='text-end'>{formatDecimal(row.ukupna_kolicina)}</td>
                    <td className='text-end'>{formatDecimal(row.prethodna_kolicina)}</td>
                    <td className={clsx('text-end fw-bold', differenceClass(getCurrentDifference(row)))}>
                      {formatDifference(getCurrentDifference(row))}
                    </td>
                    <td className='text-end'>{formatDecimal(row.prije_dvije_godine_kolicina)}</td>
                    <td className={clsx('text-end fw-bold', differenceClass(getPreviousDifference(row)))}>
                      {formatDifference(getPreviousDifference(row))}
                    </td>
                    <td className='text-end'>{formatPercent(row.promjena_postotak)}</td>
                    <td className='text-end pe-4'>{formatPercent(row.udio_postotak)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className='fw-bolder bg-light-primary border-top border-2 border-gray-400'>
                  <td className='ps-4 text-gray-900'>Ukupno</td>
                  <td className='text-end'>{formatDecimal(totals.current)}</td>
                  <td className='text-end'>{formatDecimal(totals.previous)}</td>
                  <td className={clsx('text-end', differenceClass(totals.currentDifference))}>
                    {formatDifference(totals.currentDifference)}
                  </td>
                  <td className='text-end'>{formatDecimal(totals.beforeTwoYears)}</td>
                  <td className={clsx('text-end', differenceClass(totals.previousDifference))}>
                    {formatDifference(totals.previousDifference)}
                  </td>
                  <td className={clsx('text-end', differenceClass(totals.percentChange))}>
                    {formatPercent(totals.percentChange)}
                  </td>
                  <td className='text-end pe-4'>{formatPercent(totals.share)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {activeTab === 'grafikoni' && hasData && (
          <div className='card border border-gray-300'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bolder text-gray-900'>Kolicine po drzavama i godinama</h3>
            </div>
            <div className='card-body pt-0'>
              {hasChartData ? (
                <div style={{height: 450}}>
                  <Chart type='bar' data={chartData} options={chartOptions} />
                </div>
              ) : (
                <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                  <div className='fw-bold text-gray-700'>Nema podataka za dijagram.</div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'detalji' && (
          <>
            {detailsLoading && <LoadingComponent />}

            {!detailsLoading && detailsError && (
              <div className='notice d-flex bg-light-danger rounded border-danger border border-dashed p-6'>
                <div className='fw-bold text-gray-800'>{detailsError}</div>
              </div>
            )}

            {!detailsLoading && !detailsError && !confirmedPeriod && (
              <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
                <div className='fw-bold text-gray-700'>Odaberite datume i kliknite Potvrdi.</div>
              </div>
            )}

            {!detailsLoading && !detailsError && confirmedPeriod && !hasDetailData && (
              <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                <div className='fw-bold text-gray-700'>Nema detaljnih podataka za odabrani period.</div>
              </div>
            )}

            {!detailsLoading && !detailsError && hasDetailData && (
              <div>
                <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-8 fs-6'>
                  {detailGroups.map((group) => (
                    <li key={group.drzava} className='nav-item'>
                      <button
                        type='button'
                        className={clsx('nav-link fw-bold', selectedDetailGroup?.drzava === group.drzava && 'active')}
                        onClick={() => setActiveDetailCountry(group.drzava)}
                      >
                        {group.drzava}
                      </button>
                    </li>
                  ))}
                </ul>

                {selectedDetailGroup && (
                  <div className='card border border-gray-300'>
                    <div className='card-header border-0 pt-6'>
                      <div>
                        <h3 className='card-title fw-bolder text-gray-900 mb-2'>
                          {selectedDetailGroup.drzava}
                        </h3>
                        <div className='text-muted fw-semibold fs-7'>
                          Period: {formatDisplayDate(confirmedPeriod?.datumOd ?? '')} -{' '}
                          {formatDisplayDate(confirmedPeriod?.datumDo ?? '')}
                        </div>
                      </div>
                      <span className='badge badge-light-primary fs-7 py-3 px-4'>Detalji drzave</span>
                    </div>

                    <div className='card-body pt-0'>
                      <div className='row g-4 mb-8'>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-primary'>
                            <div className='text-muted fw-semibold mb-2'>{currentYearLabel || 'Trenutna godina'}</div>
                            <div className='fs-2 fw-bolder text-gray-900'>
                              {formatDecimal(selectedDetailGroup.summary.ukupna_kolicina)}
                            </div>
                          </div>
                        </div>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-info'>
                            <div className='text-muted fw-semibold mb-2'>{previousYearLabel || 'Prethodna godina'}</div>
                            <div className='fs-2 fw-bolder text-gray-900'>
                              {formatDecimal(selectedDetailGroup.summary.prethodna_kolicina)}
                            </div>
                          </div>
                        </div>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-warning'>
                            <div className='text-muted fw-semibold mb-2'>{twoYearsAgoYearLabel || 'Prije dvije godine'}</div>
                            <div className='fs-2 fw-bolder text-gray-900'>
                              {formatDecimal(selectedDetailGroup.summary.prije_dvije_godine_kolicina)}
                            </div>
                          </div>
                        </div>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-success'>
                            <div className='text-muted fw-semibold mb-2'>Razlika {currentYearLabel} / {previousYearLabel}</div>
                            <div
                              className={clsx(
                                'fs-2 fw-bolder',
                                differenceClass(selectedDetailGroup.summary.razlika_kolicina)
                              )}
                            >
                              {formatDifference(selectedDetailGroup.summary.razlika_kolicina)}
                            </div>
                            <div className='text-muted fs-7 mt-2'>
                              {formatPercent(selectedDetailGroup.summary.promjena_postotak)}
                            </div>
                          </div>
                        </div>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-success'>
                            <div className='text-muted fw-semibold mb-2'>Razlika {previousYearLabel} / {twoYearsAgoYearLabel}</div>
                            <div
                              className={clsx(
                                'fs-2 fw-bolder',
                                differenceClass(selectedDetailGroup.summary.razlika_prije_dvije_godine_kolicina)
                              )}
                            >
                              {formatDifference(selectedDetailGroup.summary.razlika_prije_dvije_godine_kolicina)}
                            </div>
                            <div className='text-muted fs-7 mt-2'>
                              {formatPercent(selectedDetailGroup.summary.promjena_prije_dvije_godine_postotak)}
                            </div>
                          </div>
                        </div>
                        <div className='col-xl-2 col-md-4 col-sm-6'>
                          <div className='border border-gray-300 rounded p-4 h-100 bg-light-dark'>
                            <div className='text-muted fw-semibold mb-2'>Udio</div>
                            <div className='fs-2 fw-bolder text-gray-900'>
                              {formatPercent(selectedDetailGroup.summary.udio_postotak)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {selectedDetailGroup.stavke.length > 0 ? (
                        <div className='table-responsive'>
                          <table className='table table-row-dashed table-row-gray-300 align-middle gy-4 mb-0'>
                            <thead>
                              <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                                <th className='text-white ps-4 min-w-180px' rowSpan={2}>Tip panela</th>
                                {detailMatrix.columns.map((column) => (
                                  <th key={column} className='text-white text-center min-w-420px' colSpan={3}>
                                    {column}
                                  </th>
                                ))}
                                <th className='text-white text-end pe-4 min-w-120px' rowSpan={2}>Udio %</th>
                              </tr>
                              <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                                {detailMatrix.columns.map((column) => (
                                  <React.Fragment key={`years-${column}`}>
                                    <th className='text-white text-end min-w-140px'>{currentYearLabel || 'Trenutna godina'}</th>
                                    <th className='text-white text-end min-w-140px'>{previousYearLabel || 'Prethodna godina'}</th>
                                    <th className='text-white text-end min-w-140px'>{twoYearsAgoYearLabel || 'Prije dvije godine'}</th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detailMatrix.rows.map((row) => {
                                const share = getDetailShare(
                                  {ukupna_kolicina: row.total},
                                  selectedDetailGroup.summary.ukupna_kolicina
                                )

                                return (
                                  <tr key={row.panelType}>
                                    <td className='ps-4 fw-bold text-gray-900'>{row.panelType}</td>
                                    {detailMatrix.columns.map((column) => (
                                      <React.Fragment key={`${row.panelType}-${column}`}>
                                        <td className='text-end'>
                                          {formatDecimal(row.values[column]?.current ?? 0)}
                                        </td>
                                        <td className='text-end'>
                                          {formatDecimal(row.values[column]?.previous ?? 0)}
                                        </td>
                                        <td className='text-end'>
                                          {formatDecimal(row.values[column]?.beforeTwoYears ?? 0)}
                                        </td>
                                      </React.Fragment>
                                    ))}
                                    <td className='text-end pe-4'>{formatPercent(share)}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                            <tfoot>
                              <tr className='fw-bolder bg-light-primary border-top border-2 border-gray-400'>
                                <td className='ps-4 text-gray-900'>Ukupno</td>
                                {detailMatrix.columns.map((column) => (
                                  <React.Fragment key={`total-${column}`}>
                                    <td className='text-end'>
                                      {formatDecimal(detailMatrix.totals[column]?.current ?? 0)}
                                    </td>
                                    <td className='text-end'>
                                      {formatDecimal(detailMatrix.totals[column]?.previous ?? 0)}
                                    </td>
                                    <td className='text-end'>
                                      {formatDecimal(detailMatrix.totals[column]?.beforeTwoYears ?? 0)}
                                    </td>
                                  </React.Fragment>
                                ))}
                                <td className='text-end pe-4'>{formatPercent(100)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                          <div className='fw-bold text-gray-700'>Nema stavki za ovu drzavu.</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}

export default IzvjestajPoDrzavi
